const API = '/api';

const getToken = () => localStorage.getItem('funoon_token');
const getAdminToken = () => sessionStorage.getItem('funoon_admin_token');

async function request(path, options = {}) {
  const method = options.method || 'GET';
  // Customer-only endpoints (never use admin token)
  const customerPaths = ['/auth/', '/cart', '/wishlist'];
  const isCustomerPath = customerPaths.some(p => path.startsWith(p));
  // Customer mutations (POST but with customer auth)
  const customerMutations = ['/orders', '/reviews/product/', '/customers/auto-register', '/coupons/validate'];
  const isCustomerMutation = method === 'POST' && customerMutations.some(p => path.startsWith(p)) && !path.includes('/admin/');
  // Admin endpoints
  const isAdminPath = path.includes('/admin') || path.includes('/dashboard') || path.includes('/upload') || path.startsWith('/customers') && !path.includes('auto-register');
  // Admin mutations on shared resources
  const adminResources = ['/products', '/categories', '/coupons', '/offers', '/banners'];
  const isAdminMutation = method !== 'GET' && adminResources.some(p => path.startsWith(p)) && !isCustomerMutation;

  const useAdmin = (isAdminPath || isAdminMutation) && !isCustomerPath && !isCustomerMutation;
  const token = useAdmin ? getAdminToken() : getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body instanceof FormData) delete headers['Content-Type'];

  const res = await fetch(`${API}${path}`, { ...options, headers, body: options.body instanceof FormData ? options.body : options.body ? JSON.stringify(options.body) : undefined });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'حدث خطأ');
  return data;
}

const api = {
  // Auth (Customer)
  register: (body) => request('/auth/register', { method: 'POST', body }),
  login: (phone, password) => request('/auth/login', { method: 'POST', body: { phone, password } }),
  getProfile: () => request('/auth/profile'),
  updateProfile: (body) => request('/auth/profile', { method: 'PUT', body }),
  changePassword: (body) => request('/auth/password', { method: 'PUT', body }),

  // Admin Auth
  adminLogin: (username, password) => request('/admin/login', { method: 'POST', body: { username, password } }),
  getAdminProfile: () => request('/admin/profile'),
  getAdminUsers: () => request('/admin/users'),
  createAdminUser: (body) => request('/admin/users', { method: 'POST', body }),
  updateAdminUser: (id, body) => request(`/admin/users/${id}`, { method: 'PUT', body }),
  deleteAdminUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),

  // Products
  getProducts: (params = '') => request(`/products?${params}`),
  getProduct: (id) => request(`/products/${id}`),
  getAdminProducts: () => request('/products/admin/all'),
  createProduct: (body) => request('/products', { method: 'POST', body }),
  updateProduct: (id, body) => request(`/products/${id}`, { method: 'PUT', body }),
  deleteProduct: (id) => request(`/products/${id}`, { method: 'DELETE' }),

  // Categories
  getCategories: () => request('/categories'),
  getAdminCategories: () => request('/categories/admin/all'),
  createCategory: (body) => request('/categories', { method: 'POST', body }),
  updateCategory: (id, body) => request(`/categories/${id}`, { method: 'PUT', body }),
  deleteCategory: (id) => request(`/categories/${id}`, { method: 'DELETE' }),

  // Orders
  placeOrder: (body) => request('/orders', { method: 'POST', body }),
  getMyOrders: () => request('/orders/my'),
  trackOrder: (num) => request(`/orders/track/${encodeURIComponent(num)}`),
  getAdminOrders: (params = '') => request(`/orders/admin/all?${params}`),
  pollNewOrders: (since) => request(`/orders/admin/poll?since=${encodeURIComponent(since)}`),
  updateOrderStatus: (id, status) => request(`/orders/admin/${id}/status`, { method: 'PUT', body: { status } }),

  // Cart
  getCart: () => request('/cart'),
  addToCart: (body) => request('/cart', { method: 'POST', body }),
  updateCartItem: (id, quantity) => request(`/cart/${id}`, { method: 'PUT', body: { quantity } }),
  removeCartItem: (id) => request(`/cart/${id}`, { method: 'DELETE' }),
  clearCart: () => request('/cart', { method: 'DELETE' }),

  // Wishlist
  getWishlist: () => request('/wishlist'),
  addToWishlist: (productId) => request('/wishlist', { method: 'POST', body: { productId } }),
  removeFromWishlist: (productId) => request(`/wishlist/${productId}`, { method: 'DELETE' }),

  // Coupons
  validateCoupon: (code, cartTotal) => request('/coupons/validate', { method: 'POST', body: { code, cartTotal } }),
  getAdminCoupons: () => request('/coupons/admin/all'),
  createCoupon: (body) => request('/coupons', { method: 'POST', body }),
  updateCoupon: (id, body) => request(`/coupons/${id}`, { method: 'PUT', body }),
  deleteCoupon: (id) => request(`/coupons/${id}`, { method: 'DELETE' }),

  // Offers
  getOffers: () => request('/offers'),
  getAdminOffers: () => request('/offers/admin/all'),
  createOffer: (body) => request('/offers', { method: 'POST', body }),
  updateOffer: (id, body) => request(`/offers/${id}`, { method: 'PUT', body }),
  deleteOffer: (id) => request(`/offers/${id}`, { method: 'DELETE' }),

  // Banners
  getBanners: () => request('/banners'),
  getAdminBanners: () => request('/banners/admin/all'),
  createBanner: (body) => request('/banners', { method: 'POST', body }),
  updateBanner: (id, body) => request(`/banners/${id}`, { method: 'PUT', body }),
  deleteBanner: (id) => request(`/banners/${id}`, { method: 'DELETE' }),

  // Reviews
  getReviews: (productId) => request(`/reviews/product/${productId}`),
  addReview: (productId, body) => request(`/reviews/product/${productId}`, { method: 'POST', body }),

  // Settings
  getPublicSettings: () => request('/settings/public'),
  getAdminSettings: () => request('/settings/admin/all'),
  updateSetting: (key, value) => request(`/settings/admin/${key}`, { method: 'PUT', body: { value } }),
  updateFeatures: (features) => request('/settings/admin/features/bulk', { method: 'PUT', body: { features } }),
  getWhatsAppStatus: () => request('/settings/admin/whatsapp/status'),
  getWhatsAppPairingCode: (phoneNumber) => request('/settings/admin/whatsapp/pairing-code', { method: 'POST', body: { phoneNumber } }),

  // Dashboard
  getDashboard: () => request('/dashboard'),

  // Customers
  getCustomers: () => request('/customers'),
  getCustomer: (id) => request(`/customers/${id}`),
  autoRegister: (body) => request('/customers/auto-register', { method: 'POST', body }),

  // Upload
  uploadImage: (file) => { const fd = new FormData(); fd.append('image', file); return request('/upload', { method: 'POST', body: fd }); },
};

export default api;
